import React, { useState } from 'react';

// Ported from RateMyTrade/CreateTrade.js — single-team variant for the
// analyst review's Build-a-trade flow (no counterparty side).

const ff = "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif";
const POSITIONS = ['QB', 'RB', 'WR', 'TE'];

const sortPlayers = (players) =>
  [...players].sort((a, b) => {
    const ai = POSITIONS.indexOf(a.position);
    const bi = POSITIONS.indexOf(b.position);
    if (ai !== bi) return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    return (a.name || '').localeCompare(b.name || '');
  });

const formatOrdinal = (n) => {
  if (n === 1) return '1st';
  if (n === 2) return '2nd';
  if (n === 3) return '3rd';
  return `${n}th`;
};

export const formatPickDisplay = (pk) => {
  if (!pk) return '';
  const ov = pk.override;
  let core;
  if (ov?.type === 'exact') core = `${pk.season} ${pk.round}.${String(ov.value).padStart(2, '0')}`;
  else if (ov?.type === 'tier') core = `${pk.season} ${formatOrdinal(pk.round)} (${ov.value})`;
  else if (pk.slot) core = `${pk.season} ${pk.round}.${String(pk.slot).padStart(2, '0')}`;
  else if (pk.projectedSlot) core = `${pk.season} ~${pk.round}.${String(pk.projectedSlot).padStart(2, '0')}`;
  else core = `${pk.season} ${formatOrdinal(pk.round)}`;
  if (!pk.isOwn && pk.originalOwnerName) core += ` · via ${pk.originalOwnerName}`;
  return core;
};

const playerAvatarUrl = (id) => (id && /^\d+$/.test(String(id))
  ? `https://sleepercdn.com/content/nfl/players/thumb/${id}.jpg`
  : null);

const Avatar = ({ src, name, size = 28 }) => {
  const [errored, setErrored] = useState(false);
  const show = src && !errored;
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: show ? '#1A1A1A' : 'rgba(255,255,255,0.06)',
      border: '1px solid rgba(255,255,255,0.1)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0, overflow: 'hidden',
      fontSize: Math.floor(size * 0.42), fontWeight: 700, color: 'rgba(255,255,255,0.5)',
    }}>
      {show
        ? <img src={src} alt="" onError={() => setErrored(true)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : (name || '?').charAt(0).toUpperCase()}
    </div>
  );
};

// ── Pick override modal ─────────────────────────────────────────────────────
const PickOverridePicker = ({ pick, numTeams, onSave, onClose, onReset }) => {
  if (!pick) return null;
  const slotCount = Math.max(8, Math.min(20, numTeams || 12));
  const tiers = ['early', 'mid', 'late'];
  const cur = pick.override || null;
  const baseDisplay = formatPickDisplay({ ...pick, override: null });
  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.78)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', overflowY: 'auto', fontFamily: ff }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: '#0E0E10', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.1)', maxWidth: '520px', width: '100%', padding: '28px', position: 'relative' }}>
        <button onClick={onClose} aria-label="Close"
          style={{ position: 'absolute', top: '14px', right: '14px', width: '30px', height: '30px', borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)', fontSize: '16px', cursor: 'pointer', fontFamily: ff }}>×</button>
        <div style={{ fontSize: '11px', fontWeight: 500, letterSpacing: '2px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)', marginBottom: '6px' }}>Refine pick</div>
        <div style={{ fontSize: '22px', fontWeight: 700, letterSpacing: '-0.02em', color: '#fff', marginBottom: '4px' }}>{baseDisplay}</div>
        <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)', marginBottom: '22px' }}>Refine where this pick lands. Default uses our projection.</div>

        <div style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: '10px' }}>Tier</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '22px' }}>
          {tiers.map((t) => {
            const active = cur?.type === 'tier' && cur.value === t;
            return (
              <button key={t} onClick={() => onSave({ type: 'tier', value: t })}
                style={{ padding: '14px', borderRadius: '12px', cursor: 'pointer', background: active ? '#fff' : 'rgba(255,255,255,0.05)', color: active ? '#000' : 'rgba(255,255,255,0.8)', border: active ? 'none' : '1px solid rgba(255,255,255,0.1)', fontSize: '14px', fontWeight: 600, fontFamily: ff, textTransform: 'capitalize' }}>{t}</button>
            );
          })}
        </div>

        <div style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: '10px' }}>Or exact slot</div>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(6, slotCount)}, 1fr)`, gap: '6px', marginBottom: '22px' }}>
          {Array.from({ length: slotCount }, (_, i) => i + 1).map((slot) => {
            const active = cur?.type === 'exact' && cur.value === slot;
            return (
              <button key={slot} onClick={() => onSave({ type: 'exact', value: slot })}
                style={{ padding: '12px 0', borderRadius: '10px', cursor: 'pointer', background: active ? '#fff' : 'rgba(255,255,255,0.05)', color: active ? '#000' : 'rgba(255,255,255,0.8)', border: active ? 'none' : '1px solid rgba(255,255,255,0.08)', fontSize: '13px', fontWeight: 600, fontFamily: ff }}>{pick.round}.{String(slot).padStart(2, '0')}</button>
            );
          })}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
          <button onClick={onReset} disabled={!cur}
            style={{ background: 'none', border: 'none', cursor: cur ? 'pointer' : 'default', color: cur ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.18)', fontFamily: ff, fontSize: '13px', padding: '10px 4px' }}>Reset to default</button>
          <button onClick={onClose}
            style={{ background: '#fff', color: '#000', border: 'none', borderRadius: '980px', padding: '12px 24px', fontFamily: ff, fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>Done</button>
        </div>
      </div>
    </div>
  );
};

// ── Single-team picker: tabs by position + PICKS ────────────────────────────
// Props: team = { players, picks, numTeams? }, value = { players:[], picks:[] }, onChange(next)
const RosterPicker = ({ team, value, onChange, numTeams }) => {
  const [tab, setTab] = useState('QB');
  const [editing, setEditing] = useState(null); // pick being refined
  const players = team?.players || [];
  const picks = team?.picks || [];
  const v = value || { players: [], picks: [] };

  const togglePlayer = (p) => {
    const exists = v.players.some((x) => x.id === p.id);
    onChange({ ...v, players: exists ? v.players.filter((x) => x.id !== p.id) : [...v.players, p] });
  };
  const togglePick = (pk) => {
    const exists = v.picks.some((x) => x.id === pk.id);
    onChange({ ...v, picks: exists ? v.picks.filter((x) => x.id !== pk.id) : [...v.picks, pk] });
  };
  const setOverride = (pickId, override) => {
    onChange({ ...v, picks: v.picks.map((p) => (p.id === pickId ? { ...p, override } : p)) });
    setEditing((cur) => (cur ? { ...cur, override } : cur));
  };
  const isSel = (type, item) => v[type].some((x) => x.id === item.id);

  const countFor = (t) => {
    if (t === 'PICKS') return v.picks.length;
    return v.players.filter((p) => p.position === t).length;
  };

  const items = tab === 'PICKS'
    ? picks
    : sortPlayers(players.filter((p) => p.position === tab));

  const tabBtn = (t) => {
    const active = tab === t;
    const c = countFor(t);
    return (
      <button key={t} onClick={() => setTab(t)}
        style={{ position: 'relative', padding: '7px 16px', borderRadius: '980px', border: 'none', background: active ? '#fff' : 'rgba(255,255,255,0.08)', color: active ? '#000' : 'rgba(255,255,255,0.6)', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: ff }}>
        {t}
        {c > 0 && (
          <span style={{ position: 'absolute', top: '-5px', right: '-5px', background: active ? '#000' : '#fff', color: active ? '#fff' : '#000', borderRadius: '50%', width: '15px', height: '15px', fontSize: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{c}</span>
        )}
      </button>
    );
  };

  const Row = ({ children, selected, onClick }) => (
    <div onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', borderRadius: '10px', cursor: 'pointer', marginBottom: '4px', background: selected ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.04)', border: selected ? '1px solid rgba(255,255,255,0.2)' : '1px solid transparent' }}>
      {children}
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
        {[...POSITIONS, 'PICKS'].map(tabBtn)}
      </div>

      <div style={{ maxHeight: '260px', overflowY: 'auto', pr: '4px' }}>
        {items.length === 0 && (
          <div style={{ color: 'rgba(255,255,255,0.25)', fontSize: '13px', padding: '8px 0', fontFamily: ff }}>None</div>
        )}
        {items.map((it) => {
          if (tab === 'PICKS') {
            const sel = isSel('picks', it);
            const display = formatPickDisplay(sel ? v.picks.find((p) => p.id === it.id) || it : it);
            return (
              <Row key={it.id} selected={sel} onClick={() => togglePick(it)}>
                <span style={{ fontSize: '14px', flex: 1, color: sel ? '#fff' : 'rgba(255,255,255,0.55)', fontFamily: ff }}>{display}</span>
                {sel && (
                  <button onClick={(e) => { e.stopPropagation(); setEditing(v.picks.find((p) => p.id === it.id) || it); }}
                    style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)', borderRadius: '8px', padding: '4px 10px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: ff, letterSpacing: '0.5px' }}>Set pick</button>
                )}
                {sel && <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}>✓</span>}
              </Row>
            );
          }
          const sel = isSel('players', it);
          return (
            <Row key={it.id} selected={sel} onClick={() => togglePlayer(it)}>
              <Avatar src={playerAvatarUrl(it.id)} name={it.name} size={28} />
              <span style={{ fontSize: '14px', fontWeight: 500, flex: 1, color: '#fff', fontFamily: ff }}>{it.name}</span>
              {it.team && <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', fontFamily: ff }}>{it.team}</span>}
              {sel && <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}>✓</span>}
            </Row>
          );
        })}
      </div>

      {editing && (
        <PickOverridePicker
          pick={editing}
          numTeams={numTeams || (team?.numTeams)}
          onSave={(ov) => setOverride(editing.id, ov)}
          onReset={() => setOverride(editing.id, null)}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
};

export default RosterPicker;
